/**
 * Ler o registo de auditoria em português.
 *
 * O servidor guarda o mínimo: uma acção, um `targetId` e um `changes` com
 * `{ campo: [antes, depois] }`. Nomes não viajam — `toAuditEntry` manda ids
 * nus — portanto tudo o que uma pessoa consegue ler é construído aqui.
 *
 * A parte que mais engana é o `targetId`: NÃO é sempre a mesma espécie de
 * coisa. É o código do convite em `INVITE_*`, o id do canal em `CHANNEL_*`, o
 * id da mensagem em `MESSAGE_DELETE`, o id do utilizador em `MEMBER_*` — e em
 * `MESSAGE_BULK_DELETE` é o canal quando veio do botão de apagar em bloco e o
 * UTILIZADOR quando veio de um banimento com dias de mensagens. A mesma acção,
 * dois tipos de alvo. Por isso `alvoDe` tem uma preferência por acção mas
 * procura nos outros mapas a seguir, em vez de confiar cegamente na acção.
 *
 * Sem ecrã, este registo existia há meses e nunca ninguém soube quem tinha
 * apagado um canal. É isto que o torna legível — e é lógica pura de propósito,
 * para poder ser testada sem montar o painel inteiro.
 */

import type { AuditEntry } from "@greenvoice/shared";
import { deserialize, toNames } from "@greenvoice/shared";

/** Onde procurar o `targetId` primeiro. */
export type AlvoTipo = "user" | "channel" | "role" | "invite" | "message" | "server";

export interface Accao {
  /** Frase na terceira pessoa, sem o actor: «apagou o canal». */
  texto: string;
  alvo: AlvoTipo;
  /** Destaca a linha a vermelho — expulsar, banir, apagar. */
  grave?: boolean;
}

/**
 * As 19 acções do `enum AuditAction`. Se o servidor ganhar uma vigésima e este
 * mapa não a conhecer, `accaoDe` devolve a chave em bruto em vez de esconder a
 * linha: um registo com buracos é pior que um registo feio.
 */
const ACCOES: Record<string, Accao> = {
  SERVER_UPDATE: { texto: "alterou o servidor", alvo: "server" },

  CHANNEL_CREATE: { texto: "criou o canal", alvo: "channel" },
  CHANNEL_UPDATE: { texto: "alterou o canal", alvo: "channel" },
  CHANNEL_DELETE: { texto: "apagou o canal", alvo: "channel", grave: true },
  PERMISSION_OVERWRITE: { texto: "mudou permissões do canal", alvo: "channel" },

  ROLE_CREATE: { texto: "criou o cargo", alvo: "role" },
  ROLE_UPDATE: { texto: "alterou o cargo", alvo: "role" },
  ROLE_DELETE: { texto: "apagou o cargo", alvo: "role", grave: true },

  MEMBER_KICK: { texto: "expulsou", alvo: "user", grave: true },
  MEMBER_BAN: { texto: "baniu", alvo: "user", grave: true },
  MEMBER_UNBAN: { texto: "desbaniu", alvo: "user" },
  MEMBER_TIMEOUT: { texto: "silenciou", alvo: "user" },
  MEMBER_ROLE_UPDATE: { texto: "mudou os cargos de", alvo: "user" },

  MESSAGE_DELETE: { texto: "apagou uma mensagem", alvo: "message", grave: true },
  MESSAGE_BULK_DELETE: { texto: "apagou mensagens em bloco", alvo: "channel", grave: true },

  INVITE_CREATE: { texto: "criou o convite", alvo: "invite" },
  INVITE_DELETE: { texto: "revogou o convite", alvo: "invite" },

  VOICE_MOVE: { texto: "moveu de sala", alvo: "user" },
  VOICE_DISCONNECT: { texto: "desligou da voz", alvo: "user", grave: true },
};

/** Para o filtro: as acções conhecidas, pela ordem em que fazem sentido ler. */
export const ACCOES_CONHECIDAS: ReadonlyArray<{ id: string; texto: string }> = Object.entries(ACCOES).map(
  ([id, a]) => ({ id, texto: a.texto }),
);

export function accaoDe(action: string): Accao {
  return ACCOES[action] ?? { texto: action.toLowerCase().replace(/_/g, " "), alvo: "server" };
}

/**
 * Resolve ids em nomes com o que o cliente já tem em memória. Cada função
 * devolve `null` quando não conhece o id — e é essa distinção que faz a
 * procura por tipos passar ao seguinte em vez de parar num nome errado.
 */
export interface Nomeador {
  utilizador(id: string): string | null;
  canal(id: string): string | null;
  cargo(id: string): string | null;
}

/**
 * O nome do alvo, ou `null` quando não há alvo nenhum (`SERVER_UPDATE`).
 *
 * Ordem: o tipo que a acção prefere, depois os outros. Um canal apagado já não
 * está no cache — daí o `changes.name`, que o `CHANNEL_DELETE` guarda como
 * `[nome, null]` exactamente para isto (e é o mesmo campo de que o arranque do
 * servidor se serve para não voltar a semear uma sala que alguém apagou).
 */
export function alvoDe(entry: AuditEntry, nomes: Nomeador): string | null {
  const id = entry.targetId;
  if (!id) return null;

  const accao = accaoDe(entry.action);
  if (accao.alvo === "invite") return id;
  if (accao.alvo === "message") return null; // uma mensagem apagada não tem nome.

  const tentativas: AlvoTipo[] = [accao.alvo, "user", "channel", "role"];
  for (const tipo of tentativas) {
    const nome =
      tipo === "user"
        ? nomes.utilizador(id)
        : tipo === "channel"
          ? nomes.canal(id)
          : tipo === "role"
            ? nomes.cargo(id)
            : null;
    if (nome) return tipo === "channel" ? `#${nome}` : nome;
  }

  const guardado = entry.changes?.name;
  if (guardado) {
    const nome = guardado[0] ?? guardado[1];
    if (typeof nome === "string" && nome) return accao.alvo === "channel" ? `#${nome}` : nome;
  }

  return id;
}

const CAMPOS: Record<string, string> = {
  name: "Nome",
  description: "Descrição",
  topic: "Tópico",
  nickname: "Apelido",
  color: "Cor",
  position: "Posição",
  rank: "Nível",
  hoist: "Destaque na lista",
  mentionable: "Mencionável",
  nsfw: "Conteúdo sensível",
  slowmodeSeconds: "Modo lento",
  userLimit: "Limite de pessoas",
  bitrate: "Qualidade de áudio",
  parentId: "Categoria",
  channelId: "Canal",
  systemChannelId: "Canal de sistema",
  iconUrl: "Ícone",
  bannerUrl: "Banner",
  maxUses: "Usos máximos",
  expiresAt: "Expira",
  timeoutUntil: "Silenciado até",
  count: "Mensagens apagadas",
  permissions: "Permissões",
  roleIds: "Cargos",
};

export interface Alteracao {
  campo: string;
  texto: string;
}

const bonito = (nome: string): string => {
  const palavras = nome.toLowerCase().replace(/_/g, " ");
  return palavras.charAt(0).toUpperCase() + palavras.slice(1);
};

function valor(v: unknown): string {
  if (v === null || v === undefined || v === "") return "vazio";
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    // Uma data ISO por extenso não cabe numa linha de registo.
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString("pt-PT");
    return v.length > 60 ? `${v.slice(0, 57)}…` : v;
  }
  if (Array.isArray(v)) return v.length === 0 ? "nenhum" : `${v.length}`;
  return JSON.stringify(v);
}

/** `+a +b −c`, ou `null` se nada mudou de facto. */
function diffLista(antes: readonly string[], depois: readonly string[], rotulo: (id: string) => string): string | null {
  const anterior = new Set(antes);
  const seguinte = new Set(depois);
  const juntos = depois.filter((x) => !anterior.has(x)).map((x) => `+${rotulo(x)}`);
  const tirados = antes.filter((x) => !seguinte.has(x)).map((x) => `−${rotulo(x)}`);
  const todos = [...juntos, ...tirados];
  return todos.length ? todos.join(" ") : null;
}

/**
 * O `changes` em frases. Três campos não se leem como "antes → depois":
 *
 * - `permissions` são bitfields em string; o que interessa é QUE permissões
 *   entraram e saíram, não os dois números;
 * - `roleIds` são duas listas — mesma coisa, com os nomes dos cargos;
 * - `count` só tem o depois (quantas mensagens caíram).
 */
export function alteracoesDe(entry: AuditEntry, nomes: Nomeador): Alteracao[] {
  const changes = entry.changes;
  if (!changes) return [];

  const linhas: Alteracao[] = [];
  for (const [campo, par] of Object.entries(changes)) {
    if (!Array.isArray(par)) continue;
    const [antes, depois] = par;
    const rotulo = CAMPOS[campo] ?? bonito(campo);

    if (campo === "permissions") {
      const texto = diffLista(toNames(deserialize(antes as string)), toNames(deserialize(depois as string)), (n) =>
        bonito(n),
      );
      if (texto) linhas.push({ campo: rotulo, texto });
      continue;
    }

    if (campo === "roleIds" && Array.isArray(antes) && Array.isArray(depois)) {
      const texto = diffLista(antes as string[], depois as string[], (id) => nomes.cargo(id) ?? id);
      if (texto) linhas.push({ campo: rotulo, texto });
      continue;
    }

    if (campo === "count") {
      linhas.push({ campo: rotulo, texto: valor(depois) });
      continue;
    }

    if (campo === "channelId" || campo === "parentId" || campo === "systemChannelId") {
      const nome = (id: unknown) => (typeof id === "string" ? (nomes.canal(id) ?? id) : valor(id));
      if (antes === null || antes === undefined) {
        linhas.push({ campo: rotulo, texto: String(nome(depois)) });
      } else {
        linhas.push({ campo: rotulo, texto: `${nome(antes)} → ${nome(depois)}` });
      }
      continue;
    }

    // Criação (`[null, x]`) e remoção (`[x, null]`) não precisam da seta.
    if (antes === null || antes === undefined) linhas.push({ campo: rotulo, texto: valor(depois) });
    else if (depois === null || depois === undefined) linhas.push({ campo: rotulo, texto: valor(antes) });
    else linhas.push({ campo: rotulo, texto: `${valor(antes)} → ${valor(depois)}` });
  }

  return linhas;
}
